/**
 * Copyright (c) 2026 Briq-n-Braq LLC
 * All rights reserved.
 *
 * Proprietary and confidential software.
 * Unauthorized copying, modification, distribution, or use
 * of this software, in whole or in part, is strictly prohibited.
 *
 * KNX Supply Portal — portal.briq-n-braq.com
 * KNX Certified Professional | Partner No. 20248
 */

import React, { useState } from 'react';

const C = {
  navy:        '#0f1f3d',
  navyMid:     '#162d52',
  navyLight:   '#1e3a6e',
  navyBorder:  '#253d6e',
  orange:      '#F97316',
  orangeDim:   '#c2591b',
  white:       '#ffffff',
  slate:       '#94a3b8',
  slateLight:  '#cbd5e1',
  slateLighter:'#e2e8f0',
};

// ── Questions ──────────────────────────────────────────────────────────────

const QUESTIONS = [
  {
    id: 'type',
    question: 'What type of project is this?',
    subtitle: 'Project type determines which KNX product families are most applicable.',
    options: [
      { id: 'residential', icon: '⌂',  label: 'Residential', desc: 'Private home, villa, or apartment' },
      { id: 'commercial',  icon: '⊞',  label: 'Commercial',  desc: 'Retail, showroom, or public building' },
      { id: 'hotel',       icon: '⊟',  label: 'Hotel',       desc: 'Hospitality or guest accommodation' },
      { id: 'office',      icon: '⊡',  label: 'Office',      desc: 'Corporate, co-working, or campus' },
    ],
  },
  {
    id: 'size',
    question: 'What is the project size?',
    subtitle: 'Size determines the number of KNX lines, power supplies, and actuator modules required.',
    options: [
      { id: 'small',      icon: '▪▪▪',         label: 'Small',      desc: '1–5 rooms or zones' },
      { id: 'medium',     icon: '▪▪▪▪▪',       label: 'Medium',     desc: '6–15 rooms or zones' },
      { id: 'large',      icon: '▪▪▪▪▪▪▪',     label: 'Large',      desc: '16–50 rooms or zones' },
      { id: 'enterprise', icon: '▪▪▪▪▪▪▪▪▪',   label: 'Enterprise', desc: '50+ rooms, multiple floors' },
    ],
  },
  {
    id: 'goal',
    question: 'What is the primary automation goal?',
    subtitle: 'Your goal determines which sensor and actuator categories are central to the design.',
    options: [
      { id: 'lighting',  icon: '◉', label: 'Lighting Control', desc: 'Switching, dimming, scene control' },
      { id: 'hvac',      icon: '◈', label: 'HVAC & Climate',   desc: 'Heating, cooling, ventilation' },
      { id: 'security',  icon: '◆', label: 'Security & Access', desc: 'Detection, alarms, access control' },
      { id: 'full',      icon: '⬡', label: 'Full Automation',  desc: 'Lighting, HVAC, security & more' },
    ],
  },
  {
    id: 'budget',
    question: 'What is the approximate KNX budget?',
    subtitle: 'Budget determines whether premium sensors, touchscreens, and advanced logic are in scope.',
    options: [
      { id: 'entry',      icon: '€',    label: 'Entry Level', desc: 'Core function devices only' },
      { id: 'mid',        icon: '€€',   label: 'Mid-Range',   desc: 'Design-series sensors + energy metering' },
      { id: 'premium',    icon: '€€€',  label: 'Premium',     desc: 'Touchscreens, full visualisation' },
      { id: 'enterprise', icon: '€€€€', label: 'Enterprise',  desc: 'BMS integration, bespoke logic' },
    ],
  },
  {
    id: 'protocol',
    question: 'Which KNX medium or protocol will you use?',
    subtitle: 'The chosen medium affects wiring requirements, device availability, and coupler hardware.',
    options: [
      { id: 'tp',   icon: '⊝',  label: 'KNX TP',    desc: 'Twisted pair — new build or full rewire' },
      { id: 'rf',   icon: '◇',  label: 'KNX RF',    desc: 'Wireless — retrofit without new cabling' },
      { id: 'both', icon: '⊕',  label: 'TP + RF',   desc: 'Wired backbone with wireless extensions' },
      { id: 'ip',   icon: '⊙',  label: 'KNX IP',    desc: 'Ethernet backbone with TP sub-lines' },
    ],
  },
];

// ── Recommendation engine ──────────────────────────────────────────────────

function recommend(a) {
  const isTP = a.protocol === 'tp' || a.protocol === 'both' || a.protocol === 'ip';
  const isRF = a.protocol === 'rf' || a.protocol === 'both';

  const roomCounts = { small: 3, medium: 10, large: 30, enterprise: 80 };
  const rooms = roomCounts[a.size] || 10;

  const isSml  = a.size === 'small';
  const isMed  = a.size === 'medium';

  const isEntry    = a.budget === 'entry';
  const isMidPlus  = ['mid', 'premium', 'enterprise'].includes(a.budget);
  const isPremPlus = ['premium', 'enterprise'].includes(a.budget);

  const showLighting = a.goal === 'lighting' || a.goal === 'full';
  const showHVAC     = a.goal === 'hvac'     || a.goal === 'full';
  const showSecurity = a.goal === 'security' || a.goal === 'full';
  const isFull       = a.goal === 'full';

  const isRes = a.type === 'residential';
  const isCom = a.type === 'commercial';
  const isHot = a.type === 'hotel';
  const isOff = a.type === 'office';

  const rng = (lo, hi) => lo === hi ? `${lo}` : `${lo}–${hi}`;
  const items = [];

  // ── Bus infrastructure ─────────────────────────────────────────────────

  if (isTP) {
    const lo = isSml ? 1 : isMed ? 2 : 4;
    const hi = isSml ? 2 : isMed ? 4 : a.size === 'large' ? 8 : 16;
    items.push({
      id: 'psu', priority: 'Essential', abbr: 'PSU',
      name: 'Bus Power Supply (SELV 29 V DC)',
      description: 'Provides bus power to all KNX TP devices. Available in 160, 320, and 640 mA ratings; fit a line choke at each PSU output.',
      why: `Every KNX TP line requires one SELV power supply. Across ${rooms} rooms${isSml ? '' : ' and multiple lines'} you will need ${rng(lo, hi)} units — one per segment.`,
      count: rng(lo, hi) + ' units',
      examples: ['ABB SV/S 30.640.5', 'MDT SV-0416.01', 'Gira 2160 00'],
    });
  }

  items.push({
    id: 'interface', priority: 'Essential', abbr: 'INT',
    name: 'KNX USB / IP Programming Interface',
    description: 'Hardware interface connecting ETS6 on a laptop to the KNX bus for individual addressing, application download, and bus diagnostics.',
    why: 'Required at commissioning — without a USB or IP interface, ETS6 cannot communicate with any device on the bus.',
    count: '1 unit',
    examples: ['ABB IPS/S 2.1', 'Siemens 5WG1 148-1AB02', 'Gira KNX IP Interface'],
  });

  if (!isSml && isTP) {
    const lo = isMed ? 2 : 4;
    const hi = isMed ? 4 : a.size === 'large' ? 8 : 16;
    items.push({
      id: 'lc', priority: 'Essential', abbr: 'LC',
      name: 'KNX Line Couplers',
      description: 'Segments the bus into independent lines (max 64 devices each), filters telegram routing between segments, and isolates faults.',
      why: `With ${rooms} rooms spanning multiple floors, line couplers are mandatory for segmenting the bus and controlling GA routing via ETS filter tables. Estimated ${rng(lo, hi)} units.`,
      count: rng(lo, hi) + ' units',
      examples: ['ABB LK/S 4.2', 'MDT LK-0200.02', 'Siemens 5WG1 340-2AB12'],
    });
  }

  // ── Lighting control ───────────────────────────────────────────────────

  if (showLighting || isEntry) {
    const lo = Math.max(1, Math.round(rooms));
    const hi = Math.max(2, Math.round(rooms * 1.5));
    items.push({
      id: 'pb', priority: 'Essential', abbr: 'PB',
      name: 'Push-Button Sensors',
      description: 'Wall-mounted KNX sensors with integrated bus coupler for manual switching, dimming, blind control, and scene recall.',
      why: `One push-button interface per room is standard. For ${rooms} rooms you need approximately ${rng(lo, hi)} units — add more for open-plan areas and corridors.`,
      count: rng(lo, hi) + ' units',
      examples: ['Gira Event Sensor', 'MDT BE-0400.02', 'Ekinex EK-T2R-TP'],
    });
  }

  if (showLighting) {
    const circuits = rooms * 3;
    const lo = Math.max(1, Math.ceil(circuits / 8));
    const hi = Math.max(2, Math.ceil(circuits / 4));
    items.push({
      id: 'sa', priority: 'Essential', abbr: 'SA',
      name: 'Switching Actuators',
      description: 'Multi-channel DIN-rail relay modules (4, 8, or 12 ch) for on/off control of lighting circuits, socket groups, and auxiliary loads.',
      why: `Assuming ~${circuits} circuits across ${rooms} rooms (3 per room average), 4–8 channel relay modules give you ${rng(lo, hi)} modules in the distribution board.`,
      count: rng(lo, hi) + ' modules',
      examples: ['ABB SA/S 8.16.2.1', 'MDT AKK-0800.02', 'Gira 103500'],
    });
  }

  if (showLighting && isMidPlus) {
    const dimmable = rooms * 2;
    const lo = Math.max(1, Math.ceil(dimmable / 4));
    const hi = Math.max(2, Math.ceil(dimmable / 2));
    items.push({
      id: 'da', priority: 'Recommended', abbr: 'DA',
      name: 'Dimmer Actuators',
      description: 'Multi-channel DIN-rail dimmers for trailing/leading-edge, phase-cut, or DALI-bus lamp control. Essential for smooth scene transitions.',
      why: `Dimmable circuits (~${dimmable} estimated) need dedicated dimmer modules. Estimated ${rng(lo, hi)} 4-channel modules for smooth scene control and energy savings.`,
      count: rng(lo, hi) + ' modules',
      examples: ['ABB DA/S 4.600.1.1', 'MDT AKD-0400.02', 'Gira 103900'],
    });

    const blindGroups = Math.ceil(rooms * 1.2);
    const blo = Math.max(1, Math.ceil(blindGroups / 4));
    const bhi = Math.max(2, Math.ceil(blindGroups / 2));
    items.push({
      id: 'sha', priority: 'Recommended', abbr: 'SHA',
      name: 'Shutter / Blind Actuators',
      description: 'Multi-channel actuators for motorised roller blinds, shutters, awnings, and skylight vents with position and angle feedback.',
      why: `Integrated blind control enables sunshading automation, privacy scenes, and energy savings. Estimated ${rng(blo, bhi)} 2-channel modules for ${blindGroups} blind groups.`,
      count: rng(blo, bhi) + ' modules',
      examples: ['ABB JA/S 4.24.1.1', 'MDT JAL-0410.01', 'Gira 103000'],
    });
  }

  // ── HVAC & climate ─────────────────────────────────────────────────────

  if (showHVAC) {
    const lo = Math.max(1, Math.round(rooms * 0.8));
    items.push({
      id: 'rtc', priority: 'Essential', abbr: 'RTC',
      name: 'Room Temperature Controllers',
      description: 'KNX room units with integral temperature sensor, setpoint dial, operating mode selector, and optional backlit display.',
      why: `One RTC per thermal zone is the KNX standard. For ${rooms} rooms that means ${rng(lo, rooms)} units, enabling per-room setpoints, eco scheduling, and comfort/standby/frost modes.`,
      count: rng(lo, rooms) + ' units',
      examples: ['ABB FCC/S 1.1.1.1', 'MDT SCN-RTR55.01', 'Siemens QMX3.P37'],
    });
  }

  if (showHVAC && (isCom || isHot || isOff)) {
    const lo = Math.max(1, Math.round(rooms * 0.5));
    const hi = Math.round(rooms * 0.8);
    items.push({
      id: 'fcu', priority: 'Recommended', abbr: 'FCU',
      name: 'Fan Coil Unit Controllers',
      description: 'KNX actuators for 2-pipe or 4-pipe fan coil units: fan speed (3-speed or 0–10 V), water valves, and heating/cooling changeover relay.',
      why: `${isHot ? 'Hotel guest rooms' : 'Commercial and office spaces'} typically use fan coil systems requiring dedicated KNX FCU actuators per zone. Estimated ${rng(lo, hi)} units.`,
      count: rng(lo, hi) + ' units',
      examples: ['Siemens QMX3.P39', 'ABB FCU/S 1.1.1', 'Schneider MTN6503-0001'],
    });
  }

  if (showHVAC && isRes) {
    const lo = rooms;
    const hi = Math.round(rooms * 1.5);
    items.push({
      id: 'valve', priority: 'Recommended', abbr: 'VA',
      name: 'Heating Valve Actuators',
      description: 'Thermoactuators or motorised ball valves for underfloor heating manifolds and radiator circuits, driven by KNX heating actuator modules.',
      why: `Residential room-by-room heating requires one valve actuator per circuit. Estimated ${rng(lo, hi)} actuators across the ${rooms}-room installation.`,
      count: rng(lo, hi) + ' units',
      examples: ['ABB HA/S 4.2.2.1', 'MDT AKH-0400.03', 'Gira 103800'],
    });
  }

  if (showHVAC && isMidPlus) {
    items.push({
      id: 'weather', priority: 'Recommended', abbr: 'WS',
      name: 'KNX Weather Station',
      description: 'Outdoor sensor measuring wind speed, rain, sun brightness per façade (lux), ambient temperature, and twilight.',
      why: 'A weather station feeds real-time outdoor data to KNX — enabling automatic sun-tracking blind control, wind alarms for awnings, twilight switching, and outdoor-temperature-compensated heating.',
      count: '1 unit',
      examples: ['ABB WS/S 4.1', 'MDT KNX-WS01.02', 'Elsner KNX W6'],
    });
  }

  // ── Security ───────────────────────────────────────────────────────────

  if (showSecurity || (showLighting && isMidPlus)) {
    const lo = Math.max(1, Math.round(rooms * 0.5));
    const hi = Math.max(2, Math.round(rooms * 0.8));
    items.push({
      id: 'pd', priority: showSecurity ? 'Essential' : 'Recommended', abbr: 'PD',
      name: 'Presence / Motion Detectors',
      description: 'Ceiling or wall PIR / high-frequency sensors detecting occupancy for automatic lighting, HVAC setback, and intruder alerts.',
      why: `Presence detection enables lights to switch off in empty rooms and triggers security alerts. For ${rooms} rooms, ${rng(lo, hi)} detectors cover all key areas.`,
      count: rng(lo, hi) + ' units',
      examples: ['ABB PD/A 0.24.1', 'GVS MS-200 KNX', 'Ekinex EK-AG1-TP'],
    });
  }

  if (showSecurity) {
    const contacts = rooms * 2;
    const lo = Math.max(1, Math.ceil(contacts / 8));
    const hi = Math.max(2, Math.ceil(contacts / 4));
    items.push({
      id: 'bi', priority: 'Essential', abbr: 'BI',
      name: 'Binary Input Modules',
      description: 'Multi-channel modules connecting conventional volt-free contacts — door/window sensors, PIR outputs, push buttons — to the KNX bus.',
      why: `Door and window contacts need binary input modules to enter KNX. With ~${contacts} contacts across ${rooms} rooms, 4–8 channel modules give ${rng(lo, hi)} total.`,
      count: rng(lo, hi) + ' modules',
      examples: ['ABB BE/S 8.24.1.1', 'MDT BE-0800.02', 'Gira 1063 00'],
    });
  }

  // ── Advanced control ───────────────────────────────────────────────────

  if (isFull || (isMidPlus && !isSml)) {
    items.push({
      id: 'lc2', priority: isFull ? 'Essential' : 'Recommended', abbr: 'LC',
      name: 'KNX Logic Controller',
      description: 'Standalone logic module with timers, astronomical clock, if/then conditions, and sequences — programmed in ETS6 or a dedicated graphical tool.',
      why: 'Multi-condition scenes, timed sequences, HVAC schedules, and alarm logic all require a dedicated logic controller rather than per-device parameters.',
      count: '1–2 units',
      examples: ['MDT SCN-LC0100.01', 'Gira L1 (213800)', 'Loxone Miniserver'],
    });
  }

  if (isMidPlus) {
    items.push({
      id: 'ipr', priority: 'Recommended', abbr: 'IP',
      name: 'KNXnet/IP Router',
      description: 'Routes KNX telegrams over Ethernet between TP line segments and enables remote ETS commissioning and visualisation platform integration.',
      why: 'Enables remote commissioning via ETS6 over LAN/VPN, integrates with home-automation platforms, and provides the IP backbone for multi-line installations.',
      count: '1 unit',
      examples: ['ABB IPS/S 3.1.1', 'Gira X1', 'Siemens 5WG1 148-1AB02'],
    });
  }

  if (isPremPlus) {
    const lo = isSml ? 1 : isMed ? 2 : 3;
    const hi = isSml ? 2 : isMed ? 4 : 6;
    items.push({
      id: 'vis', priority: 'Recommended', abbr: 'VIS',
      name: 'Visualisation System',
      description: 'Touch panel or server-based platform for graphical floor-plan control, scene management, energy dashboards, and remote app access.',
      why: `A premium installation benefits from a touch UI for scene control and energy overview. Estimated ${rng(lo, hi)} panels${!isSml ? ' across floors' : ''}.`,
      count: rng(lo, hi) + ' panels',
      examples: ['Gira X1 + G1 panel', 'MDT Smart Tablet', 'Loxone Touch Pure'],
    });
  }

  // ── RF medium ──────────────────────────────────────────────────────────

  if (isRF) {
    const cLo = isSml ? 1 : isMed ? 1 : 2;
    const cHi = isSml ? 1 : isMed ? 2 : 4;
    items.push({
      id: 'rfc', priority: 'Essential', abbr: 'RFC',
      name: 'KNX RF Media Coupler',
      description: 'Bridges KNX RF (868 MHz bidirectional) wireless devices onto the KNX TP backbone, translating RF telegrams in both directions.',
      why: 'A media coupler is required whenever KNX RF devices are used — it is the gateway between wireless sensors and the wired bus or IP router.',
      count: rng(cLo, cHi) + ' units',
      examples: ['ABB USB-RF/S 1.1', 'MDT RF-USB01.01', 'Gira RF Interface 5392 00'],
    });

    const lo = Math.max(2, Math.round(rooms * 0.5));
    const hi = Math.round(rooms * 1.2);
    items.push({
      id: 'rfd', priority: 'Essential', abbr: 'RFD',
      name: 'KNX RF Wireless Sensors',
      description: 'Battery-powered KNX RF push buttons, temperature sensors, and motion detectors requiring no bus cabling — ideal for retrofit.',
      why: `For areas without bus wiring, RF sensors eliminate new cable runs. Estimated ${rng(lo, hi)} wireless devices covering key rooms and zones.`,
      count: rng(lo, hi) + ' units',
      examples: ['MDT BE-RF04.01', 'Gira RF dual push-button', 'ABB M-RF-USB.01'],
    });
  }

  // ── Commercial extras ──────────────────────────────────────────────────

  if ((isCom || isOff) && isMidPlus) {
    const lo = isSml ? 1 : isMed ? 2 : 4;
    const hi = isSml ? 2 : isMed ? 4 : 8;
    items.push({
      id: 'em', priority: 'Recommended', abbr: 'EM',
      name: 'KNX Energy Meters',
      description: 'DIN-rail electricity meters with KNX interface for real-time active power monitoring, energy logging, and multi-tenant sub-metering.',
      why: `Commercial and office buildings require sub-metering for tenant billing, energy management systems (EMS), and BMS/BACS integration. Estimated ${rng(lo, hi)} meters.`,
      count: rng(lo, hi) + ' units',
      examples: ['ABB B24 112-100', 'Schneider iEM3155 KNX', 'Eastron SDM120KNX'],
    });
  }

  return items;
}

// ── Sub-components ─────────────────────────────────────────────────────────

function ProgressBar({ step, total }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: '1.75rem' }}>
      {Array.from({ length: total }).map((_, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <div style={{
              flex: 1,
              height: 2,
              background: i <= step ? C.orange : C.navyBorder,
              transition: 'background 0.25s',
            }} />
          )}
          <div style={{
            width: 30, height: 30,
            borderRadius: '50%',
            background: i < step ? C.orange : i === step ? C.navyLight : C.navyMid,
            border: `2px solid ${i <= step ? C.orange : C.navyBorder}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700,
            color: i < step ? C.white : i === step ? C.orange : C.slate,
            flexShrink: 0,
            transition: 'all 0.25s',
            zIndex: 1,
          }}>
            {i < step ? '✓' : i + 1}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

function OptionCard({ option, isSelected, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: isSelected
          ? `linear-gradient(135deg, ${C.orange} 0%, ${C.orangeDim} 100%)`
          : hovered ? C.navyLight : C.navyMid,
        border: `2px solid ${isSelected ? C.orange : hovered ? C.orange + '66' : C.navyBorder}`,
        borderRadius: 10,
        padding: '1rem',
        cursor: 'pointer',
        textAlign: 'left',
        color: C.white,
        transition: 'all 0.12s ease',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        boxShadow: isSelected ? `0 0 0 3px ${C.orange}33` : 'none',
      }}
    >
      <span style={{
        fontSize: 22,
        lineHeight: 1,
        color: isSelected ? C.white : C.orange,
        letterSpacing: '0.05em',
      }}>
        {option.icon}
      </span>
      <span style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>
        {option.label}
      </span>
      <span style={{
        fontSize: 12,
        color: isSelected ? 'rgba(255,255,255,0.78)' : C.slate,
        lineHeight: 1.4,
        transition: 'color 0.12s',
      }}>
        {option.desc}
      </span>
    </button>
  );
}

function PriorityBadge({ priority }) {
  const styles = {
    Essential:   { bg: '#16a34a1a', border: '#16a34a55', color: '#4ade80' },
    Recommended: { bg: `${C.orange}1a`, border: `${C.orange}55`, color: C.orange },
    Optional:    { bg: `${C.navyLight}88`, border: C.navyBorder, color: C.slate },
  };
  const s = styles[priority] || styles.Optional;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700,
      padding: '2px 8px', borderRadius: 4,
      background: s.bg, border: `1px solid ${s.border}`, color: s.color,
      letterSpacing: '0.04em', flexShrink: 0,
      textTransform: 'uppercase',
    }}>
      {priority}
    </span>
  );
}

function RecommendationCard({ item }) {
  return (
    <div style={{
      background: C.navyMid,
      border: `1.5px solid ${C.navyBorder}`,
      borderRadius: 10,
      padding: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: 9,
      transition: 'border-color 0.15s',
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8, flexShrink: 0,
          background: `${C.orange}22`,
          border: `1.5px solid ${C.orange}44`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 800, color: C.orange, letterSpacing: '-0.3px',
        }}>
          {item.abbr}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13.5, color: C.white, lineHeight: 1.3, marginBottom: 5 }}>
            {item.name}
          </div>
          <PriorityBadge priority={item.priority} />
        </div>
        <div style={{
          background: `${C.orange}1a`,
          border: `1px solid ${C.orange}44`,
          borderRadius: 6, padding: '4px 10px', flexShrink: 0,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 9, color: C.orange, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Est. Qty
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.white, marginTop: 1, whiteSpace: 'nowrap' }}>
            {item.count}
          </div>
        </div>
      </div>

      {/* Description */}
      <p style={{ margin: 0, color: C.slateLight, fontSize: 12.5, lineHeight: 1.55 }}>
        {item.description}
      </p>

      {/* Why panel */}
      <div style={{
        background: `${C.navy}88`, borderRadius: 7,
        padding: '9px 11px', border: `1px solid ${C.navyBorder}`,
      }}>
        <div style={{
          fontSize: 10, color: C.orange, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 5,
        }}>
          Why you need it
        </div>
        <p style={{ margin: 0, color: C.slateLighter, fontSize: 12.5, lineHeight: 1.55 }}>
          {item.why}
        </p>
      </div>

      {/* Examples */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <div style={{ fontSize: 9.5, color: C.slate, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>
            Example products
          </div>
          <div style={{ fontSize: 11.5, color: C.slateLight }}>
            {item.examples.join(' · ')}
          </div>
        </div>
      </div>
    </div>
  );
}

function AnswerChip({ question, answers }) {
  const q = QUESTIONS.find(q => q.id === question);
  const opt = q?.options.find(o => o.id === answers[question]);
  if (!opt) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      background: `${C.orange}18`,
      border: `1px solid ${C.orange}44`,
      borderRadius: 6, padding: '4px 10px',
    }}>
      <span style={{ fontSize: 13, color: C.orange }}>{opt.icon}</span>
      <div>
        <div style={{ fontSize: 9, color: C.slate, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {q.id}
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.white }}>
          {opt.label}
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function KNXProductGuide() {
  const [step, setStep]         = useState(0);
  const [answers, setAnswers]   = useState({});
  const [flash, setFlash]       = useState(null); // briefly highlights the selected option

  const isComplete = step >= QUESTIONS.length;
  const recs       = isComplete ? recommend(answers) : [];
  const essential  = recs.filter(r => r.priority === 'Essential').length;
  const recommended = recs.filter(r => r.priority === 'Recommended').length;

  function handleSelect(qId, optId) {
    setFlash(optId);
    setTimeout(() => {
      setAnswers(prev => ({ ...prev, [qId]: optId }));
      setFlash(null);
      setStep(s => s + 1);
    }, 220);
  }

  function handleReset() {
    setStep(0);
    setAnswers({});
    setFlash(null);
  }

  return (
    <div style={{
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      background: C.navy,
      minHeight: '100vh',
      color: C.white,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* ── Header ── */}
      <div style={{
        background: C.navyMid,
        borderBottom: `3px solid ${C.orange}`,
        padding: '0.875rem 1.75rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            background: C.orange, color: C.white, fontWeight: 800,
            fontSize: 17, padding: '4px 10px', borderRadius: 6, letterSpacing: '-0.5px',
          }}>
            BB
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, letterSpacing: '-0.3px', lineHeight: 1.2 }}>
              Briq-n-Braq
            </div>
            <div style={{ color: C.slate, fontSize: 11, marginTop: 1 }}>
              KNX Product Selection Guide
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: C.orange, fontWeight: 600, fontSize: 12.5 }}>
            KNX Partner No. 20248
          </div>
          <div style={{ color: C.slate, fontSize: 11, marginTop: 2 }}>
            ETS6 · KNX TP · KNXnet/IP
          </div>
        </div>
      </div>

      <div style={{ flex: 1, padding: '2rem 1.75rem', maxWidth: 900, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>

        {!isComplete ? (
          /* ── Questionnaire ── */
          <>
            <div style={{ marginBottom: '1.5rem' }}>
              <h1 style={{ margin: '0 0 0.3rem', fontSize: 21, fontWeight: 700, letterSpacing: '-0.4px' }}>
                Which KNX products do I need?
              </h1>
              <p style={{ margin: 0, color: C.slate, fontSize: 13 }}>
                Answer 5 quick questions to get a tailored product stack with estimated component counts.
              </p>
            </div>

            <ProgressBar step={step} total={QUESTIONS.length} />

            {/* Question card */}
            <div style={{
              background: C.navyMid,
              border: `2px solid ${C.navyBorder}`,
              borderRadius: 14,
              padding: '1.75rem',
            }}>
              <div style={{
                display: 'inline-block',
                background: `${C.orange}22`,
                border: `1px solid ${C.orange}55`,
                color: C.orange, fontSize: 11, fontWeight: 700,
                padding: '2px 9px', borderRadius: 4,
                textTransform: 'uppercase', letterSpacing: '0.07em',
                marginBottom: '0.75rem',
              }}>
                Question {step + 1} of {QUESTIONS.length}
              </div>

              <h2 style={{ margin: '0 0 0.35rem', fontSize: 19, fontWeight: 700, letterSpacing: '-0.3px' }}>
                {QUESTIONS[step].question}
              </h2>
              <p style={{ margin: '0 0 1.5rem', color: C.slate, fontSize: 13 }}>
                {QUESTIONS[step].subtitle}
              </p>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.75rem',
              }}>
                {QUESTIONS[step].options.map(opt => (
                  <OptionCard
                    key={opt.id}
                    option={opt}
                    isSelected={flash === opt.id}
                    onClick={() => handleSelect(QUESTIONS[step].id, opt.id)}
                  />
                ))}
              </div>
            </div>

            {/* Back button */}
            {step > 0 && (
              <button
                onClick={() => setStep(s => s - 1)}
                style={{
                  marginTop: '1rem',
                  background: 'none',
                  border: `1px solid ${C.navyBorder}`,
                  color: C.slate, fontSize: 13, cursor: 'pointer',
                  borderRadius: 7, padding: '6px 16px',
                  transition: 'color 0.12s, border-color 0.12s',
                }}
              >
                ← Back
              </button>
            )}
          </>
        ) : (
          /* ── Results ── */
          <>
            {/* Results header */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <h1 style={{ margin: '0 0 0.3rem', fontSize: 21, fontWeight: 700, letterSpacing: '-0.4px' }}>
                    Your Recommended Product Stack
                  </h1>
                  <p style={{ margin: 0, color: C.slate, fontSize: 13 }}>
                    Based on your answers —{' '}
                    <span style={{ color: '#4ade80', fontWeight: 600 }}>{essential} Essential</span>
                    {recommended > 0 && (
                      <> · <span style={{ color: C.orange, fontWeight: 600 }}>{recommended} Recommended</span></>
                    )}
                    {' '}· {recs.length} categories total
                  </p>
                </div>
                <button
                  onClick={handleReset}
                  style={{
                    background: C.navyMid,
                    border: `1.5px solid ${C.navyBorder}`,
                    color: C.slateLight, fontSize: 12.5, cursor: 'pointer',
                    borderRadius: 7, padding: '7px 16px',
                    fontWeight: 500, flexShrink: 0,
                  }}
                >
                  ← Start over
                </button>
              </div>

              {/* Answer summary chips */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: '1rem' }}>
                {QUESTIONS.map(q => (
                  <AnswerChip key={q.id} question={q.id} answers={answers} />
                ))}
              </div>
            </div>

            {/* Recommendation grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
              gap: '0.875rem',
            }}>
              {recs.map(item => (
                <RecommendationCard key={item.id} item={item} />
              ))}
            </div>

            {/* Footer note */}
            <div style={{
              marginTop: '1.75rem',
              padding: '1rem 1.25rem',
              background: C.navyMid,
              border: `1px solid ${C.navyBorder}`,
              borderRadius: 10,
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <span style={{ fontSize: 18, color: C.orange, flexShrink: 0 }}>◈</span>
              <p style={{ margin: 0, color: C.slate, fontSize: 12.5, lineHeight: 1.6 }}>
                <span style={{ color: C.white, fontWeight: 600 }}>Component counts are estimates</span>{' '}
                based on typical KNX design ratios. A full site survey and ETS6 topology plan will produce exact quantities.
                Contact <span style={{ color: C.orange, fontWeight: 600 }}>Briq-n-Braq (KNX Partner No. 20248)</span> for a detailed bill of materials.
              </p>
            </div>
          </>
        )}
      </div>

      {/* ── Footer ── */}
      <div style={{
        padding: '0.875rem 1.75rem',
        background: C.navyMid,
        borderTop: `2px solid ${C.navyBorder}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontSize: 11.5, color: C.slate, flexWrap: 'wrap', gap: 8,
      }}>
        <div>
          <span style={{ color: C.white, fontWeight: 600 }}>Briq-n-Braq</span>
          {' · '}KNX Certified Partner No.{' '}
          <span style={{ color: C.orange, fontWeight: 600 }}>20248</span>
        </div>
        <div>ETS6 · KNX TP · KNXnet/IP · IEC 14543-3</div>
      </div>
    </div>
  );
}
